import { Box, Button, Text, theme } from '@everlywell/ui-kit';
import AddToCartButton from 'components/AddToCartButton';
import PriceV2 from 'components/PricingV2/PriceV2/PriceV2';
import PromoDiscountPrice from 'components/PricingV2/PromoDiscountPrice/PromoDiscountPrice';
import ProductCardV2 from 'components/ProductCardV2';
import { CardTags } from 'components/ProductDisplayGridV2/ProductDisplayGridV2';
import { navigate } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import React, { useCallback, useMemo } from 'react';
import { trackEva, trackEvaProductClick } from 'utils/eva';
import {
  findActivePromotionForProduct,
  Promo,
  promoAppliesToProduct,
} from 'utils/helpers/activePromoHelpers';

import { sanitizeAgentText } from '../../lib/formatters';
import { getEvaProductHref } from '../../lib/productHref';
import type { ProductData } from '../products/products.types';

export type EvaExperience = 'search' | 'conversation';

export type EvaProductCardProps = {
  product: ProductData;
  advertisedPromos: Promo[];
  description?: string;
  experience?: EvaExperience;
  onCardClick?: () => void;
};

export default function EvaProductCard({
  product,
  advertisedPromos,
  description,
  experience = 'conversation',
  onCardClick,
}: EvaProductCardProps): JSX.Element {
  const activePromo = findActivePromotionForProduct(
    product.productId,
    advertisedPromos,
  );

  const productHasPromo =
    activePromo &&
    promoAppliesToProduct(
      product.productId,
      activePromo,
      product.promotionable,
    );

  const sanitizedDescription = useMemo(
    () => (description ? sanitizeAgentText(description) : ''),
    [description],
  );

  /**
   * Contentful drives the CTA: products with a `productCtaUrl` (e.g. Caris
   * Detect) link out instead of adding to cart, matching the shared product
   * grid and carousel.
   */
  const productContext = product.contentfulProduct001?.metadata?.context;
  const ctaUrl = productContext?.productCtaUrl;

  const pdpHref = useMemo(
    () => productContext?.url ?? getEvaProductHref(product.slug),
    [productContext?.url, product.slug],
  );

  const productPayload = useMemo(
    () => ({
      id: product.productId,
      variant_id: product.variantId,
      slug: product.slug,
      name: product.displayName,
      price: product.price,
      url: pdpHref,
    }),
    [product, pdpHref],
  );

  const handleClick = () => {
    trackEvaProductClick(product.slug);
    trackEva('Eva Product Card Clicked', {
      product: productPayload,
      experience,
    });
    onCardClick?.();
  };

  const handleAddToCart = useCallback(() => {
    trackEva('Added Eva Search Product to Cart', {
      product: productPayload,
      experience,
    });
  }, [productPayload, experience]);

  const handlePdpLinkClick = useCallback(() => {
    if (!ctaUrl) return;

    trackEva('Eva Product Card PDP CTA Clicked', {
      product: productPayload,
      experience,
    });
    // Closing the modal first repaints the page behind it before the route
    // swaps, so keep it up until the PDP is on screen.
    void navigate(ctaUrl).then(() => onCardClick?.());
  }, [productPayload, experience, onCardClick, ctaUrl]);

  const cta = ctaUrl ? (
    <Button
      data-testid="eva-pdp-link-button"
      size="sm"
      minWidth={['100px', '120px']}
      height={['32px', '40px']}
      backgroundColor="eggplant.base"
      color="white"
      fontWeight="medium"
      _hover={{ backgroundColor: 'violet.dark' }}
      onClick={handlePdpLinkClick}
    >
      {productContext?.productCtaLabel ?? 'Learn more'}
    </Button>
  ) : (
    <AddToCartButton
      product={product}
      clickAnalyticsData={{
        label: `Eva ${
          experience === 'search' ? 'Search' : 'Conversation'
        } - Add to cart`,
        experience,
      }}
      onClick={handleAddToCart}
    />
  );

  return (
    <Box
      height="100%"
      sx={{
        '& [data-testid="pip-add-to-cart-button"]': {
          backgroundColor: 'eggplant.base !important',
          _hover: {
            backgroundColor: 'violet.dark !important',
          },
        },
      }}
    >
      <ProductCardV2
        handleOnClick={handleClick}
        variant="default"
        tag={
          <Box display={['none', 'flex']} gap="0.5" alignItems="center">
            <CardTags
              product={product}
              activePromoForProduct={productHasPromo ? activePromo : undefined}
            />
          </Box>
        }
        image={
          <>
            <Box display={['block', 'none']} height="100%">
              {product?.boxImage?.gatsbyImageData && (
                <GatsbyImage
                  image={product.boxImage.gatsbyImageData}
                  alt=""
                  imgStyle={{ objectFit: 'contain' }}
                  style={{ height: '100%' }}
                />
              )}
            </Box>
            <Box display={['none', 'block']} height="100%">
              {product?.productCardImage?.gatsbyImageData ? (
                <GatsbyImage
                  image={product.productCardImage.gatsbyImageData}
                  alt=""
                  imgStyle={{ objectFit: 'contain' }}
                  style={{ height: '100%' }}
                />
              ) : product?.boxImage?.gatsbyImageData ? (
                <GatsbyImage
                  image={product.boxImage.gatsbyImageData}
                  alt=""
                  imgStyle={{ objectFit: 'contain' }}
                  style={{ height: '100%' }}
                />
              ) : null}
            </Box>
          </>
        }
        pricing={<PriceV2 dollarAmount={product.price} />}
        pricingOverride={
          productHasPromo ? (
            <PromoDiscountPrice
              price={product.price}
              promo={activePromo}
              productId={product.productId}
              promotionable={product.promotionable}
              subscribeSelected={false}
              isTextReduced={true}
              isOutOfStock={product.outOfStock}
              promoText={activePromo ? undefined : product.promoText}
            />
          ) : null
        }
        cta={cta}
        linkTo={pdpHref}
      >
        <Box
          display={['flex', 'none']}
          marginBottom={2}
          gap="0.5"
          alignItems="center"
        >
          <CardTags
            product={product}
            activePromoForProduct={productHasPromo ? activePromo : undefined}
          />
        </Box>
        <Text
          fontSize={['lg', '3xl']}
          color="black"
          fontWeight="medium"
          marginY="2"
          textAlign={['left', 'center']}
        >
          {product.displayName}
        </Text>
        {sanitizedDescription ? (
          <Box
            fontSize="sm"
            color={theme.colors.tints.darkGrey}
            textAlign={['left', 'center']}
            dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
          />
        ) : (
          <Text
            fontSize="sm"
            color={theme.colors.tints.darkGrey}
            textAlign={['left', 'center']}
          >
            {product.shortDescription}
          </Text>
        )}
      </ProductCardV2>
    </Box>
  );
}
